import React, { useEffect, useState, useRef } from 'react';
import api from '../services/api';

interface Music {
  id_musica: number;
  id_banda: number;
  id_album: number;
  nome_musica: string;
  compositor: string;
  ano_lancamento: number;
  url_musica: string;
  url_imagem: string;
  duracao: string;
  data_adicao: string;
  letra: string;
  status: string;
  nome_banda: string; // Nome da banda vindo do backend
}

const AprovMusica: React.FC = () => {
  const [musicas, setMusicas] = useState<{ pendente: Music[]; aprovado: Music[]; reprovado: Music[] }>({
    pendente: [],
    aprovado: [],
    reprovado: [],
  });
  const [expandedStatus, setExpandedStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const audioRefs = useRef<{ [key: number]: HTMLAudioElement | null }>({});

  useEffect(() => {
    fetchMusics();
  }, []);

  const fetchMusics = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('🔄 Fazendo requisição para buscar músicas...');
      const response = await api.get('/admin/musics');

      // LOG PRINCIPAL: Resposta completa do servidor
      console.log('📦 Resposta completa do servidor:', response);
      console.log('🎵 Dados das músicas:', response.data);

      // LOG: Verificar estrutura de cada status
      if (response.data.pendente) {
        console.log('⏳ Músicas pendentes:', response.data.pendente);
        console.log('⏳ Primeira música pendente (exemplo):', response.data.pendente[0]);
      }

      if (response.data.aprovado) {
        console.log('✅ Músicas aprovadas:', response.data.aprovado);
        console.log('✅ Primeira música aprovada (exemplo):', response.data.aprovado[0]);
      }

      if (response.data.reprovado) {
        console.log('❌ Músicas reprovadas:', response.data.reprovado);
        console.log('❌ Primeira música reprovada (exemplo):', response.data.reprovado[0]);
      }

      // LOG: Verificar especificamente os campos relacionados à banda
      const todasMusicas = [
        ...(response.data.pendente || []),
        ...(response.data.aprovado || []),
        ...(response.data.reprovado || [])
      ];

      if (todasMusicas.length > 0) {
        console.log('🎸 Verificando campos de banda na primeira música:');
        const primeiraMusica = todasMusicas[0];
        console.log('- id_banda:', primeiraMusica.id_banda);
        console.log('- nome_banda:', primeiraMusica.nome_banda);
        console.log('- Todas as chaves da primeira música:', Object.keys(primeiraMusica));

        // Verificar quantas músicas têm nome da banda
        const musicasComNomeBanda = todasMusicas.filter(m => m.nome_banda);
        console.log(`🎸 ${musicasComNomeBanda.length} de ${todasMusicas.length} músicas têm 'nome_banda'`);

        if (musicasComNomeBanda.length > 0) {
          console.log('🎸 Exemplo de música com nome_banda:', musicasComNomeBanda[0]);
        }
      }

      setMusicas(response.data);
    } catch (err) {
      console.error('❌ Erro ao carregar músicas:', err);

      // LOG: Detalhes do erro
      const axiosErr = err as any;
      if (axiosErr.response) {
        console.error('📡 Status da resposta:', axiosErr.response?.status);
        console.error('📡 Dados da resposta de erro:', axiosErr.response?.data);
        console.error('📡 Headers da resposta:', axiosErr.response?.headers);
      }

      setError('Erro ao carregar músicas.');
    } finally {
      setLoading(false);
    }
  };

  const updateMusicStatus = async (id_musica: number, status: string) => {
    try {
      console.log(`🔄 Atualizando status da música ${id_musica} para "${status}"`);

      const response = await api.put(
        `/admin/update/${id_musica}/status`,
        { status }
      );

      console.log('📦 Resposta da atualização de status:', response);

      if (response.status === 200) {
        alert(`Status da música atualizado para "${status}" com sucesso!`);
        fetchMusics(); // Atualiza a lista após a mudança de status
      } else {
        alert('Falha ao atualizar status da música.');
      }
    } catch (err) {
      console.error('❌ Erro ao atualizar status:', err);
      alert('Erro ao atualizar status da música. Verifique sua conexão e tente novamente.');
    }
  };

  const toggleStatus = (status: string) => {
    console.log(`🔄 Alternando visualização do status: ${status}`);
    setExpandedStatus(expandedStatus === status ? null : status);
  };

  // Função para pausar todos os outros áudios quando um novo começar
  const handleAudioPlay = (currentMusicId: number) => {
    Object.entries(audioRefs.current).forEach(([musicId, audioElement]) => {
      if (audioElement && parseInt(musicId) !== currentMusicId) {
        audioElement.pause();
      }
    });
  };

  // Função para registrar referência do áudio
  const setAudioRef = (musicId: number, element: HTMLAudioElement | null) => {
    audioRefs.current[musicId] = element;
  };

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Gerenciamento de Músicas</h1>
      {loading && <p style={styles.message}>Carregando...</p>}
      {error && <p style={{ ...styles.message, color: '#dc3545' }}>{error}</p>}

      {!loading && (
        <div>
          {(['pendente', 'aprovado', 'reprovado'] as const).map((status) => (
            <div key={status} style={styles.statusSection}>
              <button
                style={{
                  ...styles.statusButton,
                  backgroundColor: expandedStatus === status ? '#ff6600' : '#f2f2f2',
                  color: expandedStatus === status ? 'white' : '#333',
                }}
                onClick={() => toggleStatus(status)}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)} ({musicas[status]?.length || 0})
              </button>
              {expandedStatus === status && (
                <div style={styles.musicList}>
                  {(musicas[status] || []).map((music) => (
                    <div key={music.id_musica} style={styles.musicCard}>
                      <div style={styles.musicInfo}>
                        <img
                          src={music.url_imagem}
                          alt={music.nome_musica}
                          style={styles.musicImage}
                        />
                        <div>
                          <h3 style={styles.musicTitle}>{music.nome_musica}</h3>
                          <p style={styles.musicSubtitle}>
                            <strong>Banda:</strong> {music.nome_banda || 'Banda não informada'}
                          </p>
                          <p style={styles.musicSubtitle}>
                            <strong>Compositor:</strong> {music.compositor || 'Desconhecido'} |
                            <strong> Ano:</strong> {music.ano_lancamento || 'Não informado'}
                          </p>
                        </div>
                      </div>
                      <div style={styles.actions}>
                        {status === 'pendente' && (
                          <>
                            <button
                              style={{ ...styles.actionButton, backgroundColor: '#28a745' }}
                              onClick={() => updateMusicStatus(music.id_musica, 'aprovado')}
                            >
                              Aprovar
                            </button>
                            <button
                              style={{ ...styles.actionButton, backgroundColor: '#dc3545' }}
                              onClick={() => updateMusicStatus(music.id_musica, 'reprovado')}
                            >
                              Reprovar
                            </button>
                          </>
                        )}
                        <audio
                          controls
                          style={styles.audioPlayer}
                          ref={(el) => setAudioRef(music.id_musica, el)}
                          onPlay={() => handleAudioPlay(music.id_musica)}
                        >
                          <source src={music.url_musica} type="audio/mpeg" />
                          Seu navegador não suporta o player de áudio.
                        </audio>
                      </div>
                    </div>
                  ))}
                  {(musicas[status] || []).length === 0 && (
                    <p style={styles.message}>Nenhuma música encontrada.</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: '20px',
    maxWidth: '1200px', // Aumentado de 800px para 1200px
    margin: '0 auto',
    backgroundColor: '#f9f9f9',
    borderRadius: '8px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  },
  title: {
    fontSize: '24px',
    color: '#333',
    textAlign: 'center' as 'center',
    marginBottom: '20px',
  },
  message: {
    fontSize: '16px',
    textAlign: 'center' as 'center',
    color: '#555',
  },
  statusSection: {
    marginBottom: '20px',
  },
  statusButton: {
    width: '100%',
    padding: '10px',
    textAlign: 'left' as 'left',
    fontSize: '18px',
    fontWeight: 'bold',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    marginBottom: '10px',
    transition: 'background-color 0.3s ease',
  },
  musicList: {
    paddingLeft: '10px',
  },
  musicCard: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '15px', // Aumentado de 10px para 15px
    marginBottom: '10px',
    backgroundColor: 'white',
    borderRadius: '8px', // Aumentado de 4px para 8px
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    minHeight: '80px', // Adicionado altura mínima
  },
  musicInfo: {
    display: 'flex',
    alignItems: 'center',
    flex: '1', // Permite que a info ocupe o espaço disponível
    marginRight: '20px', // Espaçamento entre info e ações
  },
  musicImage: {
    width: '60px', // Aumentado de 50px para 60px
    height: '60px', // Aumentado de 50px para 60px
    borderRadius: '8px', // Aumentado de 4px para 8px
    marginRight: '15px', // Aumentado de 10px para 15px
    objectFit: 'cover' as 'cover', // Garante que a imagem não deforme
  },
  musicTitle: {
    fontSize: '16px',
    color: '#333',
    marginBottom: '5px',
  },
  musicSubtitle: {
    fontSize: '14px',
    color: '#777',
    marginBottom: '3px',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px', // Aumentado de 10px para 15px
    flexShrink: 0, // Impede que a área de ações encolha
  },
  actionButton: {
    padding: '8px 12px',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'background-color 0.3s ease',
  },
  audioPlayer: {
    width: '400px', // Aumentado de 300px para 400px
    maxWidth: '100%',
    height: '40px', // Adicionada altura específica
  },
};

export default AprovMusica;